import { useResetPassword } from '@/api/auth/auth.hooks.ts';
import {
  ResetPasswordFormData,
  resetPasswordSchema,
} from '@/validation/reset-password.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContainer } from '../components/auth-container.component';

const inputSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'white',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
    '&:hover': { bgcolor: 'white' },
    '&.Mui-focused': { bgcolor: 'white' },
  },
};

export function AuthResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [done, setDone] = useState(false);

  const resetPassword = useResetPassword();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = handleSubmit((data) => {
    if (!token) return;
    resetPassword.mutate(
      { token, password: data.password },
      {
        onSuccess: () => setDone(true),
        onError: (error: AxiosError) => {
          const responseMessage = (error?.response?.data as { message?: string })
            ?.message;
          const message =
            error?.response?.status === 400 && responseMessage
              ? responseMessage
              : 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
          setError('password', { message });
        },
      },
    );
  });

  if (!token) {
    return (
      <>
        <Typography
          variant="h5"
          sx={{
            color: '#02344C',
            fontWeight: 700,
            mb: 0.75,
            textAlign: 'center',
            letterSpacing: '-0.01em',
          }}
        >
          Ungültiger Link
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 1,
            textAlign: 'center',
            maxWidth: 340,
            lineHeight: 1.6,
          }}
        >
          Dieser Link zum Zurücksetzen des Passworts ist unvollständig oder
          ungültig. Bitte fordern Sie einen neuen an.
        </Typography>
        <AuthContainer>
          <Button
            onClick={() => navigate('/auth/forgot-password')}
            fullWidth
            variant="contained"
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Neuen Link anfordern
          </Button>
        </AuthContainer>
      </>
    );
  }

  if (done) {
    return (
      <>
        <Typography
          variant="h5"
          sx={{
            color: '#02344C',
            fontWeight: 700,
            mb: 0.75,
            textAlign: 'center',
            letterSpacing: '-0.01em',
          }}
        >
          Passwort geändert
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 1,
            textAlign: 'center',
            maxWidth: 340,
            lineHeight: 1.6,
          }}
        >
          Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt mit
          Ihrem neuen Passwort anmelden.
        </Typography>
        <AuthContainer>
          <Button
            onClick={() => navigate('/auth')}
            fullWidth
            variant="contained"
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Zur Anmeldung
          </Button>
        </AuthContainer>
      </>
    );
  }

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          color: '#02344C',
          fontWeight: 700,
          mb: 0.75,
          textAlign: 'center',
          letterSpacing: '-0.01em',
        }}
      >
        Neues Passwort
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
          textAlign: 'center',
          maxWidth: 340,
          lineHeight: 1.6,
        }}
      >
        Wählen Sie ein neues Passwort für Ihr Konto.
      </Typography>
      <AuthContainer component="form" onSubmit={onSubmit}>
        <TextField
          id="password"
          label="Neues Passwort"
          type="password"
          variant="filled"
          fullWidth
          required
          autoFocus
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
          sx={inputSx}
        />
        <TextField
          id="password-confirm"
          label="Passwort wiederholen"
          type="password"
          variant="filled"
          fullWidth
          required
          {...register('confirmPassword')}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          sx={inputSx}
        />
        {!!errors.root && (
          <FormHelperText error>{errors.root.message}</FormHelperText>
        )}
        <Box sx={{ width: '100%', mt: 1 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={resetPassword.isPending}
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Passwort speichern{' '}
            {resetPassword.isPending && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Button>
        </Box>
      </AuthContainer>
    </>
  );
}
